import { useState, useEffect, useCallback, useRef } from 'react';

export const DEFAULT_INITIAL_ASSOCIATION_RESULTS = 20;

function mergeAssociationResults(previousResults, nextResults) {
  const mergedResults = [];
  const seenKeys = new Set();

  [...previousResults, ...nextResults].forEach((item, index) => {
    if (!item) {
      return;
    }

    const key = String(item.id ?? item.text ?? index);
    if (seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    mergedResults.push(item);
  });

  return mergedResults;
}

export function useDropdownDismiss(containerRef, onDismiss, enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onDismiss();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, enabled, onDismiss]);
}

export function useInfiniteScrollLoader({
  containerRef,
  sentinelRef,
  onLoadMore,
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const maybeLoadMore = () => {
      if (container.scrollHeight - container.scrollTop - container.clientHeight < 72) {
        onLoadMore();
      }
    };

    const frameId = window.requestAnimationFrame(maybeLoadMore);
    container.addEventListener('scroll', maybeLoadMore, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      container.removeEventListener('scroll', maybeLoadMore);
    };
  }, [containerRef, enabled, onLoadMore]);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const container = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      {
        root: container,
        rootMargin: '0px 0px 160px 0px',
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, enabled, onLoadMore, sentinelRef]);
}

export function useAssociationSearch({
  config,
  isOpen,
  search,
  allowEmptySearch = false,
  minimumInputLength = 1,
  initialPageSize = DEFAULT_INITIAL_ASSOCIATION_RESULTS,
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const appendLockRef = useRef(false);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Effect 1 — déclenché par changement de recherche/ouverture : repart de l'offset 0
  useEffect(() => {
    const trimmedSearch = search.trim();
    const canLoad = isOpen && (
      (trimmedSearch.length === 0 && allowEmptySearch)
      || trimmedSearch.length >= minimumInputLength
    );

    setOffset(0);
    setHasMore(false);
    hasMoreRef.current = false;
    appendLockRef.current = false;

    if (!canLoad) {
      setResults([]);
      setLoading(false);
      loadingRef.current = false;
      return undefined;
    }

    const pageSize = trimmedSearch.length === 0
      ? Math.min(config.pageSize, initialPageSize)
      : config.pageSize;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      loadingRef.current = true;

      try {
        const params = new URLSearchParams({
          manager: config.manager,
          method: config.method,
          q: trimmedSearch,
          n: String(pageSize),
          first: '0',
        });
        if (config.extra) {
          params.set('extra', config.extra);
        }
        if (config.exclude) {
          params.set('exclude', config.exclude);
        }
        if (config.descFields) {
          params.set('desc_fields', config.descFields);
        }
        if (config.fieldValues) {
          params.set('field_values', config.fieldValues);
        }
        if (config.textField) {
          params.set('text_field', config.textField);
        }
        const response = await fetch(
          `${config.baseUrl}cst-admin-tools/association-options?${params}`,
          { signal: controller.signal },
        );
        const data = await response.json();
        const items = Array.isArray(data.results) ? data.results : [];
        setResults(items);
        const nextHasMore = items.length === pageSize;
        setHasMore(nextHasMore);
        hasMoreRef.current = nextHasMore;
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setResults([]);
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          loadingRef.current = false;
          appendLockRef.current = false;
        }
      }
    }, trimmedSearch.length === 0 ? 120 : 300);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [allowEmptySearch, config.baseUrl, config.exclude, config.extra, config.manager, config.method, config.pageSize, initialPageSize, isOpen, minimumInputLength, search]);

  // Effect 2 — déclenché uniquement quand l'offset avance (load-more)
  useEffect(() => {
    if (offset === 0) {
      return undefined;
    }

    const trimmedSearch = search.trim();
    const canLoad = isOpen && (
      (trimmedSearch.length === 0 && allowEmptySearch)
      || trimmedSearch.length >= minimumInputLength
    );
    if (!canLoad) {
      appendLockRef.current = false;
      return undefined;
    }

    const pageSize = trimmedSearch.length === 0
      ? Math.min(config.pageSize, initialPageSize)
      : config.pageSize;

    const controller = new AbortController();
    let active = true;

    (async () => {
      setLoading(true);
      loadingRef.current = true;
      try {
        const params = new URLSearchParams({
          manager: config.manager,
          method: config.method,
          q: trimmedSearch,
          n: String(pageSize),
          first: String(offset),
        });
        if (config.extra) {
          params.set('extra', config.extra);
        }
        if (config.exclude) {
          params.set('exclude', config.exclude);
        }
        if (config.descFields) {
          params.set('desc_fields', config.descFields);
        }
        if (config.fieldValues) {
          params.set('field_values', config.fieldValues);
        }
        if (config.textField) {
          params.set('text_field', config.textField);
        }
        const response = await fetch(
          `${config.baseUrl}cst-admin-tools/association-options?${params}`,
          { signal: controller.signal },
        );
        const data = await response.json();
        const items = Array.isArray(data.results) ? data.results : [];
        if (active) {
          setResults((prev) => mergeAssociationResults(prev, items));
          const nextHasMore = items.length === pageSize;
          setHasMore(nextHasMore);
          hasMoreRef.current = nextHasMore;
        }
      } catch (error) {
        if (active && error?.name !== 'AbortError') {
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (active) {
          setLoading(false);
          loadingRef.current = false;
          appendLockRef.current = false;
        }
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
    // Seul offset dans les deps : on lit search/isOpen/config par closure au moment du déclenchement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const loadMore = useCallback(() => {
    if (appendLockRef.current || loadingRef.current || !hasMoreRef.current) {
      return;
    }

    const trimmedSearch = search.trim();
    const pageSize = trimmedSearch.length === 0
      ? Math.min(config.pageSize, initialPageSize)
      : config.pageSize;

    appendLockRef.current = true;
    setOffset((prev) => prev + pageSize);
  }, [search, config.pageSize, initialPageSize]);

  return { loading, results, setResults, hasMore, loadMore };
}
